import { useState } from 'react';
import CamposValores, { valoresIniciais } from '@/components/leads/CamposValores';
import { exigeMotivo, motivoValido, validarCorrecao, validarEntrada } from '@/leads/valores';

/**
 * ★ WD-c2 — o diálogo de EDITAR os valores de um lead já fechado.
 *
 * ★ Ele NÃO redesenha os campos: monta o `CamposValores`, o MESMO componente que o `DialogoEtapa` usa ao
 * mover o lead para "Fechou". Dois momentos, um conjunto de campos — se fossem duas cópias, divergiriam no
 * primeiro ajuste. Guarda de fonte cobra isso.
 *
 * ★ O motivo é pedido só quando `exigeMotivo` diz — isto é, quando o TOTAL DA VENDA muda e havia total antes.
 * Acertar entrada, código, nome ou oportunidade salva direto.
 *
 * Este componente NÃO decide nada — quem decide é `@/leads/valores`.
 */
export default function DialogoValores({ lead, clinica = null, aoFechar, aoSalvar }) {
  const [valores, setValores] = useState(() => valoresIniciais(lead));
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  /**
   * ★ O aviso do total aparece ENQUANTO se digita, não só ao salvar — quem zera o total precisa ler ali
   * que o caminho é mudar a etapa para "Não fechou", antes de tentar salvar e levar um erro seco.
   * Quem decide o texto é `validarCorrecao`; aqui só se mostra.
   */
  const avisoTotal = validarCorrecao({ etapa: lead?.etapa, valorFechado: valores.valorFechado });

  const pedeMotivo = exigeMotivo({
    valorFechadoAnterior: lead?.valorFechado,
    valorFechadoNovo: valores.valorFechado,
  });

  async function enviar(evento) {
    evento.preventDefault();
    setErro(null);
    if (avisoTotal) { setErro(avisoTotal); return; }
    const invalidoEntrada = validarEntrada({ valorFechado: valores.valorFechado, valorEntrada: valores.valorEntrada });
    if (invalidoEntrada) { setErro(invalidoEntrada); return; }
    if (pedeMotivo && !motivoValido(valores.motivo)) { setErro('Escreva o motivo da correção do total.'); return; }
    setSalvando(true);
    try {
      await aoSalvar(valores);
    } catch (e) {
      setErro(e?.message ?? 'Não foi possível salvar os valores.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="modal-fundo" role="dialog" aria-modal="true" aria-label="Editar valores do fechamento">
      <form className="modal card dialogo-valores" onSubmit={enviar}>
        <h3>Editar valores</h3>
        <CamposValores valores={valores} aoMudar={setValores} lead={lead} clinica={clinica} disabled={salvando} autoFocus />
        {avisoTotal && <p className="aviso dv-aviso">{avisoTotal}</p>}
        {erro && <p className="erro dv-erro">{erro}</p>}
        <div className="modal-acoes">
          <button type="button" className="btn-secundario" onClick={aoFechar} disabled={salvando}>Cancelar</button>
          <button type="submit" className="btn-primario" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar'}</button>
        </div>
      </form>
    </div>
  );
}
